import { showToast } from '../components/ui/Toaster.jsx';

import { formatCurrency, formatDate, formatNumber } from './formatters.js';

// jsPDF is ~350 kB minified - load it lazily so it stays out of the main chunk
// and is only fetched when a PDF is actually built (download / e-mail attachment).
// Single-flight: concurrent first calls share one import; a failed fetch (offline,
// stale chunk hash after a redeploy) clears the cache so the next click retries.
let jsPdfPromise = null;
function loadJsPdf() {
  if (!jsPdfPromise) {
    const pending = import('jspdf').then((m) => m.jsPDF);
    pending.catch(() => {
      if (jsPdfPromise === pending) jsPdfPromise = null;
    });
    jsPdfPromise = pending;
  }
  return jsPdfPromise;
}

const MARGIN = 20;
const PAGE_W = 210;

function header(doc, settings, title, ref) {
  const cfg = settings || {};
  let y = MARGIN;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text(cfg.companyName || 'RSZ ERP', MARGIN, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(110);
  const lines = [
    cfg.addressLine1,
    [cfg.zip, cfg.city].filter(Boolean).join(' '),
    cfg.email,
    cfg.iban ? `IBAN: ${cfg.iban}` : null,
    // Swiss UID/VAT number, e.g. "CHE-123.456.789 MWST" - value carries its own prefix.
    cfg.vatNumber || null,
  ].filter(Boolean);
  let ly = y + 6;
  for (const l of lines) {
    doc.text(String(l), MARGIN, ly);
    ly += 4.5;
  }

  // Title block (right-aligned)
  doc.setTextColor(20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(title, PAGE_W - MARGIN, y + 2, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(90);
  doc.text(ref, PAGE_W - MARGIN, y + 9, { align: 'right' });
  doc.setTextColor(20);

  return Math.max(ly, y + 16) + 4;
}

function partyBlock(doc, y, label, party, meta) {
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(label.toUpperCase(), MARGIN, y);
  doc.setTextColor(20);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(party?.name || '-', MARGIN, y + 6);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(110);
  // Postal address (wrapped), then email - a customer-facing doc needs the address.
  let leftY = y + 11;
  if (party?.billingAddress) {
    const addrLines = doc.splitTextToSize(String(party.billingAddress), 85);
    doc.text(addrLines, MARGIN, leftY);
    leftY += addrLines.length * 4.5;
  }
  if (party?.email) {
    doc.text(String(party.email), MARGIN, leftY);
    leftY += 4.5;
  }
  doc.setTextColor(20);

  // meta (right)
  doc.setFontSize(9);
  let my = y;
  for (const [k, v] of meta) {
    doc.setTextColor(120);
    doc.text(k, PAGE_W - MARGIN - 45, my, { align: 'left' });
    doc.setTextColor(20);
    doc.text(String(v), PAGE_W - MARGIN, my, { align: 'right' });
    my += 5;
  }
  return Math.max(leftY + 1, Math.max(y + 16, my)) + 4;
}

function hr(doc, y) {
  doc.setDrawColor(210);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  return y + 5;
}

/**
 * Hoehe eines A4-Blatts.
 *
 * Bis hierher stand oben nur die BREITE, und genau das war der Fehler: ohne
 * Hoehe gibt es keinen Seitenumbruch, und jsPDF zeichnet klaglos ueber die
 * Blattkante hinaus ins Nichts. Eine Rechnung mit dreissig Positionen verlor
 * still ihre letzten Zeilen und ihren Summenblock. Aufgefallen waere das
 * niemandem hier - sondern der Kundschaft des Kunden, bei der Reklamation.
 */
const PAGE_H = 297;

/* Was unten frei bleibt: Rand plus Platz fuer die Seitenzahl. */
const FUSSRAUM = 22;

/**
 * Sorgt dafuer, dass fuer die naechsten `hoehe` Millimeter noch Platz ist.
 *
 * Reicht er nicht, beginnt ein neues Blatt - und `kopf` setzt dort die
 * Spaltenueberschriften noch einmal. Ohne das staende auf Seite zwei eine
 * Zahlenkolonne ohne Beschriftung, und niemand wuesste, welche Spalte die
 * Menge ist und welche der Preis.
 *
 * @param {number} hoehe  Was als Naechstes gezeichnet wird, in Millimetern.
 *                        Fuer einen Block, der nicht zerrissen werden darf,
 *                        die Hoehe des GANZEN Blocks angeben.
 */
function raum(doc, y, hoehe, kopf = null) {
  if (y + hoehe <= PAGE_H - FUSSRAUM) {
    return y;
  }
  doc.addPage();
  return kopf ? kopf(doc, MARGIN) : MARGIN;
}

/**
 * Seitenzahlen - erst ganz am Schluss, weil ein Dokument vorher nicht weiss,
 * wie viele Blaetter es hat.
 *
 * Nur bei mehr als einem Blatt. Auf einer einseitigen Rechnung waere "Seite 1
 * von 1" Laerm; auf einer dreiseitigen ist es das Einzige, woran der
 * Empfaenger merkt, dass ihm ein Blatt fehlt.
 */
function seitenzahlen(doc, t) {
  const gesamt = doc.getNumberOfPages();
  if (gesamt < 2) {
    return;
  }
  for (let seite = 1; seite <= gesamt; seite += 1) {
    doc.setPage(seite);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(130);
    const text = String(t?.('pdf.page') || 'Seite {n} von {total}')
      .replace('{n}', String(seite))
      .replace('{total}', String(gesamt));
    doc.text(text, PAGE_W - MARGIN, PAGE_H - 10, { align: 'right' });
    doc.setTextColor(20);
  }
  doc.setPage(gesamt);
}

async function buildInvoicePdf({ invoice, customer, settings, t }) {
  const jsPDF = await loadJsPdf();
  const inv = invoice;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = header(doc, settings, (t('invoice.print.heading') || 'Invoice'), inv.invoiceNumber);
  // Internal state never prints on the customer copy - drafts get a stamp instead.
  if (inv.status === 'DRAFT') {
    doc.setFontSize(9);
    doc.setTextColor(180);
    doc.text(String(t('pdf.draftMark') || 'DRAFT').toUpperCase(), PAGE_W - MARGIN, MARGIN + 15, { align: 'right' });
    doc.setTextColor(20);
  }
  y = partyBlock(doc, y, t('invoice.print.billTo') || 'Bill to', customer, [
    [t('col.date') || 'Date', formatDate(inv.invoiceDate)],
    [t('col.due') || 'Due', formatDate(inv.dueDate)],
  ]);
  y = hr(doc, y);

  // Table header - als Funktion, damit er auf jedem Folgeblatt wieder dasteht.
  const spaltenkopf = (d, ny) => {
    d.setFontSize(8);
    d.setTextColor(120);
    d.text(t('detail.lineDescription') || 'Description', MARGIN, ny);
    d.text(t('detail.lineQty') || 'Qty', 108, ny, { align: 'right' });
    d.text(t('detail.lineUnitPrice') || 'Unit', 138, ny, { align: 'right' });
    d.text(t('vat.rateCol') || 'VAT %', 160, ny, { align: 'right' });
    d.text(t('detail.lineTotal') || 'Total', PAGE_W - MARGIN, ny, { align: 'right' });
    d.setTextColor(20);
    const nach = hr(d, ny + 2);
    d.setFontSize(9);
    return nach;
  };
  y = spaltenkopf(doc, y);

  for (const line of inv.lines || []) {
    y = raum(doc, y, 6, spaltenkopf);
    doc.text(String(line.description || ''), MARGIN, y, { maxWidth: 70 });
    doc.text(formatNumber(line.quantity), 108, y, { align: 'right' });
    doc.text(formatCurrency(line.unitPrice, inv.currency), 138, y, { align: 'right' });
    doc.text(`${Number(line.taxRate || 0).toFixed(1)} %`, 160, y, { align: 'right' });
    doc.text(formatCurrency(line.lineAmount, inv.currency), PAGE_W - MARGIN, y, { align: 'right' });
    y += 6;
  }
  /* Der Summenblock wird NICHT zerrissen. Eine Rechnung, deren Endbetrag
     allein auf einem zweiten Blatt steht, sieht aus wie eine Mahnung. */
  y = raum(doc, y, 5 + 5 * 6);
  y = hr(doc, y);

  const outstanding = Math.max(0, Number(inv.totalAmount) - Number(inv.paidAmount));
  const totals = [
    [t('vat.net') || 'Net', formatCurrency(inv.netAmount ?? inv.totalAmount, inv.currency), false],
    [t('vat.tax') || 'VAT', formatCurrency(inv.taxAmount ?? 0, inv.currency), false],
    [t('invoice.print.total') || 'Total', formatCurrency(inv.totalAmount, inv.currency), true],
    [t('col.paid') || 'Paid', formatCurrency(inv.paidAmount, inv.currency), false],
    [t('detail.outstanding') || 'Outstanding', formatCurrency(outstanding, inv.currency), true],
  ];
  for (const [k, v, bold] of totals) {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 11 : 9);
    doc.text(k, 150, y, { align: 'right' });
    doc.text(v, PAGE_W - MARGIN, y, { align: 'right' });
    y += 6;
  }
  doc.setFont('helvetica', 'normal');

  seitenzahlen(doc, t);
  return { doc, filename: `Rechnung_${inv.invoiceNumber}.pdf` };
}

async function buildDunningPdf({ notice, invoice, customer, settings, t }) {
  const jsPDF = await loadJsPdf();
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const ref = `${t('dunning.level') || 'Level'} ${notice.level} · ${notice.invoiceNumber}`;
  let y = header(doc, settings, t('dunning.print.heading') || 'Payment reminder', ref);
  y = partyBlock(doc, y, t('invoice.print.billTo') || 'Bill to', customer, [
    [t('dunning.col.date') || 'Date', formatDate(notice.noticeDate)],
    [t('col.due') || 'Due', invoice ? formatDate(invoice.dueDate) : '-'],
    [t('dunning.col.invoice') || 'Invoice', notice.invoiceNumber],
  ]);
  y = hr(doc, y);

  doc.setFontSize(10);
  doc.text(t('dunning.print.greeting') || '', MARGIN, y);
  y += 6;
  const body = doc.splitTextToSize(t('dunning.print.body') || '', PAGE_W - 2 * MARGIN);
  doc.text(body, MARGIN, y);
  y += body.length * 5 + 4;

  const amountDue = Number(notice.amountDue || 0);
  const fee = Number(notice.fee || 0);
  const rows = [
    [t('dunning.print.amountDue') || 'Amount due', formatCurrency(amountDue, notice.currency), false],
    [t('dunning.col.fee') || 'Fee', formatCurrency(fee, notice.currency), false],
    [t('dunning.print.total') || 'Total', formatCurrency(amountDue + fee, notice.currency), true],
  ];
  y = raum(doc, y, rows.length * 6);
  for (const [k, v, bold] of rows) {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 11 : 10);
    doc.text(k, 150, y, { align: 'right' });
    doc.text(v, PAGE_W - MARGIN, y, { align: 'right' });
    y += 6;
  }
  doc.setFont('helvetica', 'normal');
  y += 2;

  const due = new Date(notice.noticeDate);
  due.setDate(due.getDate() + 10);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`${t('dunning.print.payBy') || 'Payable by'}: ${formatDate(due.toISOString().slice(0, 10))}`, MARGIN, y);
  doc.setFont('helvetica', 'normal');
  y += 8;
  doc.setFontSize(9);
  doc.text(t('dunning.print.closing') || '', MARGIN, y);
  y += 5;
  doc.text((settings && settings.companyName) || 'RSZ ERP', MARGIN, y);

  seitenzahlen(doc, t);
  return { doc, filename: `Mahnung_${notice.invoiceNumber}_Stufe${notice.level}.pdf` };
}

/**
 * EBICS INI-letter. Printed, signed by hand, and mailed to the bank so it can
 * activate the subscriber. Contains the subscriber IDs and the SHA-256 hashes of
 * the three public keys (A006 signature, X002 authentication, E002 encryption).
 * `hashes` come from the backend init-keys response (no private material).
 */
async function buildEbicsLetterPdf({ ids, hashes, settings, t }) {
  const jsPDF = await loadJsPdf();
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = header(doc, settings, t('ebics.letter.heading') || 'EBICS Initialisation Letter', ids.bankName || '');

  doc.setFontSize(10);
  doc.setTextColor(20);
  const meta = [
    [t('ebics.letter.date') || 'Date', formatDate(new Date().toISOString().slice(0, 10))],
    [t('ebics.hostId') || 'Host-ID', ids.hostId || '-'],
    [t('ebics.partnerId') || 'Partner-ID', ids.partnerId || '-'],
    [t('ebics.userId') || 'User-ID', ids.userId || '-'],
  ];
  for (const [k, v] of meta) {
    doc.setTextColor(120);
    doc.text(String(k), MARGIN, y);
    doc.setTextColor(20);
    doc.text(String(v), MARGIN + 45, y);
    y += 6;
  }
  y = hr(doc, y);

  const sections = [
    ['A006', t('ebics.letter.a006') || 'Electronic signature (A006)'],
    ['X002', t('ebics.letter.x002') || 'Authentication (X002)'],
    ['E002', t('ebics.letter.e002') || 'Encryption (E002)'],
  ];
  for (const [key, label] of sections) {
    y = raum(doc, y, 22);
    const h = hashes?.[key];
    if (!h) {
      continue;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(label, MARGIN, y);
    y += 5;
    doc.setFont('courier', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(40);
    const wrapped = doc.splitTextToSize(`SHA-256: ${h.hex || h.base64}`, PAGE_W - 2 * MARGIN);
    doc.text(wrapped, MARGIN, y);
    y += wrapped.length * 4.5 + 4;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(20);
  }

  y = hr(doc, y);
  doc.setFontSize(9);
  doc.setTextColor(90);
  const note = doc.splitTextToSize(
    t('ebics.letter.note') ||
      'Please verify these public-key hashes and activate the subscriber. Signed by:',
    PAGE_W - 2 * MARGIN,
  );
  doc.text(note, MARGIN, y);
  y += note.length * 4.5 + 16;
  doc.setTextColor(20);
  doc.text(t('ebics.letter.signature') || 'Date, Place, Signature', MARGIN, y);
  doc.setDrawColor(160);
  doc.line(MARGIN, y - 4, MARGIN + 80, y - 4);

  seitenzahlen(doc, t);
  return { doc, filename: `EBICS_INI_Brief_${ids.userId || 'subscriber'}.pdf` };
}

/**
 * Download wrapper for onClick handlers: builds and saves the PDF, surfacing
 * failures (e.g. stale lazy chunk after a redeploy, offline) as a toast
 * instead of an unhandled rejection. `args.t` is the caller's translator.
 */
async function downloadPdf(build, args) {
  try {
    const { doc, filename } = await build(args);
    doc.save(filename);
  } catch (error) {
    showToast(error?.message || args?.t?.('common.loadError') || 'PDF failed');
  }
}

/**
 * In-browser preview instead of a download: opens the PDF in a new tab via a
 * blob URL. The tab is opened synchronously (inside the click gesture) so
 * popup blockers allow it; the blob is assigned once the PDF is built.
 */
async function previewPdf(build, args) {
  const tab = typeof window !== 'undefined' ? window.open('', '_blank') : null;
  try {
    const { doc } = await build(args);
    const url = doc.output('bloburl');
    if (tab) {
      tab.location = url;
    } else {
      window.open(url, '_blank');
    }
  } catch (error) {
    tab?.close();
    showToast(error?.message || args?.t?.('common.loadError') || 'PDF failed');
  }
}

export function downloadEbicsLetterPdf(args) {
  return downloadPdf(buildEbicsLetterPdf, args);
}

async function buildDeliveryPdf({ delivery, customer, settings, t }) {
  const jsPDF = await loadJsPdf();
  const d = delivery;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = header(doc, settings, (t('delivery.print.heading') || 'Delivery note'), d.deliveryNumber);
  const party = customer || { name: d.shipToAddress || '-' };
  y = partyBlock(doc, y, t('delivery.print.shipTo') || 'Ship to', party, [
    [t('delivery.deliveryDate') || 'Delivery date', formatDate(d.deliveryDate)],
    [t('delivery.shippingDate') || 'Shipping date', d.shippingDate ? formatDate(d.shippingDate) : '-'],
    [t('delivery.carrier') || 'Carrier', d.carrier || '-'],
  ]);
  if (d.shipToAddress) {
    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text(String(d.shipToAddress), MARGIN, y);
    doc.setTextColor(20);
    y += 5;
  }
  y = hr(doc, y);

  doc.setFontSize(9);
  doc.setTextColor(110);
  const shipBits = [
    d.trackingNumber ? `${t('delivery.tracking') || 'Tracking'}: ${d.trackingNumber}` : null,
    d.packageCount != null ? `${t('delivery.packages') || 'Packages'}: ${d.packageCount}` : null,
    d.totalWeight != null ? `${t('delivery.weight') || 'Weight'}: ${d.totalWeight} kg` : null,
  ].filter(Boolean);
  if (shipBits.length) {
    doc.text(shipBits.join('   ·   '), MARGIN, y);
    y += 6;
    doc.setTextColor(20);
    y = hr(doc, y);
  }
  doc.setTextColor(20);

  const spaltenkopf = (dd, ny) => {
    dd.setFontSize(8);
    dd.setTextColor(120);
    dd.text(t('detail.lineDescription') || 'Description', MARGIN, ny);
    dd.text(t('delivery.qtyDelivered') || 'Delivered', PAGE_W - MARGIN, ny, { align: 'right' });
    dd.setTextColor(20);
    const nach = hr(dd, ny + 2);
    dd.setFontSize(9);
    return nach;
  };
  y = spaltenkopf(doc, y);

  for (const line of d.lines || []) {
    y = raum(doc, y, 6, spaltenkopf);
    doc.text(String(line.description || ''), MARGIN, y, { maxWidth: 140 });
    doc.text(String(line.quantity), PAGE_W - MARGIN, y, { align: 'right' });
    y += 6;
  }

  seitenzahlen(doc, t);
  return { doc, filename: `Lieferschein_${d.deliveryNumber}.pdf` };
}

async function buildCreditNotePdf({ creditNote, invoice, customer, settings, t }) {
  const jsPDF = await loadJsPdf();
  const cn = creditNote;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const headingKey = cn.type === 'PURCHASE' ? 'creditNote.print.headingVendor' : 'creditNote.print.heading';
  let y = header(doc, settings, (t(headingKey) || 'Credit note'), cn.creditNoteNumber);
  y = partyBlock(doc, y, t('invoice.print.billTo') || 'Bill to', customer, [
    [t('col.date') || 'Date', formatDate(cn.creditDate)],
    [t('creditNote.print.forInvoice') || 'For invoice', invoice?.invoiceNumber || '-'],
  ]);
  y = hr(doc, y);

  const spaltenkopf = (dd, ny) => {
    dd.setFontSize(8);
    dd.setTextColor(120);
    dd.text(t('detail.lineDescription') || 'Description', MARGIN, ny);
    dd.text(t('detail.lineQty') || 'Qty', 108, ny, { align: 'right' });
    dd.text(t('detail.lineUnitPrice') || 'Unit', 138, ny, { align: 'right' });
    dd.text(t('vat.rateCol') || 'VAT %', 160, ny, { align: 'right' });
    dd.text(t('detail.lineTotal') || 'Total', PAGE_W - MARGIN, ny, { align: 'right' });
    dd.setTextColor(20);
    const nach = hr(dd, ny + 2);
    dd.setFontSize(9);
    return nach;
  };
  y = spaltenkopf(doc, y);

  for (const line of cn.lines || []) {
    y = raum(doc, y, 6, spaltenkopf);
    doc.text(String(line.description || ''), MARGIN, y, { maxWidth: 70 });
    doc.text(formatNumber(line.quantity), 108, y, { align: 'right' });
    doc.text(formatCurrency(line.unitPrice, cn.currency), 138, y, { align: 'right' });
    doc.text(`${Number(line.taxRate || 0).toFixed(1)} %`, 160, y, { align: 'right' });
    doc.text(formatCurrency(line.lineAmount, cn.currency), PAGE_W - MARGIN, y, { align: 'right' });
    y += 6;
  }
  y = raum(doc, y, 5 + 3 * 6);
  y = hr(doc, y);
  const cnTotals = [
    [t('vat.net') || 'Net', formatCurrency(cn.netAmount ?? cn.totalAmount, cn.currency), false],
    [t('vat.tax') || 'VAT', formatCurrency(cn.taxAmount ?? 0, cn.currency), false],
    [t('creditNote.print.total') || 'Credit total', formatCurrency(cn.totalAmount, cn.currency), true],
  ];
  for (const [k, v, bold] of cnTotals) {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 11 : 9);
    doc.text(k, 150, y, { align: 'right' });
    doc.text(v, PAGE_W - MARGIN, y, { align: 'right' });
    y += 6;
  }
  doc.setFont('helvetica', 'normal');

  seitenzahlen(doc, t);
  return { doc, filename: `Gutschrift_${cn.creditNoteNumber}.pdf` };
}

// Shared layout for sales documents (order confirmation, quote): letterhead,
// party block, optional letter-style intro, a Desc/Qty/Unit/Total line table,
// grand total, optional notes (VAT/validity) and a sign-off. `draftMark` stamps
// an internal draft label instead of leaking a Status row onto the document.
// "5 Std." / "1 pauschal" - lines without a unit (legacy) stay a bare number.
function qtyWithUnit(t, line) {
  const qty = formatNumber(line.quantity);
  if (!line.unit) return qty;
  const key = `uom.short.${line.unit}`;
  const label = t(key);
  return `${qty} ${label === key ? line.unit : label}`;
}

async function buildSalesDocPdf({ settings, t, title, ref, party, partyLabel, meta, intro, lines, currency, total, totals, notes, closing, draftMark, filename }) {
  const jsPDF = await loadJsPdf();
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = header(doc, settings, title, ref);
  if (draftMark) {
    doc.setFontSize(9);
    doc.setTextColor(180);
    doc.text(String(draftMark).toUpperCase(), PAGE_W - MARGIN, MARGIN + 15, { align: 'right' });
    doc.setTextColor(20);
  }
  y = partyBlock(doc, y, partyLabel || t('invoice.print.billTo') || 'Bill to', party, meta);
  if (intro) {
    doc.setFontSize(10);
    const introLines = doc.splitTextToSize(String(intro), PAGE_W - 2 * MARGIN);
    doc.text(introLines, MARGIN, y);
    y += introLines.length * 5 + 3;
  }
  y = hr(doc, y);

  // With VAT totals the table gains a per-line rate column (invoice layout).
  const withTax = totals && Number(totals.tax) !== 0;
  const QTY_X = withTax ? 112 : 120;
  const UNIT_X = withTax ? 140 : 155;

  const spaltenkopf = (dd, ny) => {
    dd.setFontSize(8);
    dd.setTextColor(120);
    dd.text(t('detail.lineDescription') || 'Description', MARGIN, ny);
    dd.text(t('detail.lineQty') || 'Qty', QTY_X, ny, { align: 'right' });
    dd.text(t('detail.lineUnitPrice') || 'Unit', UNIT_X, ny, { align: 'right' });
    if (withTax) dd.text(t('vat.rateCol') || 'VAT %', 162, ny, { align: 'right' });
    dd.text(t('detail.lineTotal') || 'Total', PAGE_W - MARGIN, ny, { align: 'right' });
    dd.setTextColor(20);
    const nach = hr(dd, ny + 2);
    dd.setFontSize(9);
    return nach;
  };
  y = spaltenkopf(doc, y);

  for (const line of lines || []) {
    y = raum(doc, y, 6, spaltenkopf);
    doc.text(String(line.description || ''), MARGIN, y, { maxWidth: withTax ? 82 : 90 });
    doc.text(qtyWithUnit(t, line), QTY_X, y, { align: 'right' });
    doc.text(formatCurrency(line.unitPrice, currency), UNIT_X, y, { align: 'right' });
    if (withTax) doc.text(`${Number(line.taxRate || 0).toFixed(1)} %`, 162, y, { align: 'right' });
    doc.text(formatCurrency(line.lineAmount, currency), PAGE_W - MARGIN, y, { align: 'right' });
    y += 6;
  }
  /* Netto, MWST und Endbetrag gehoeren zusammen aufs selbe Blatt. */
  y = raum(doc, y, withTax ? 5 + 6 + 7 + 8 : 5 + 8);
  y = hr(doc, y);

  if (withTax) {
    doc.setFontSize(9.5);
    doc.text(t('vat.net') || 'Net', 150, y, { align: 'right' });
    doc.text(formatCurrency(totals.net, currency), PAGE_W - MARGIN, y, { align: 'right' });
    y += 6;
    doc.text(t('vat.tax') || 'VAT', 150, y, { align: 'right' });
    doc.text(formatCurrency(totals.tax, currency), PAGE_W - MARGIN, y, { align: 'right' });
    y += 7;
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(t('invoice.print.total') || 'Total', 150, y, { align: 'right' });
  doc.text(formatCurrency(total, currency), PAGE_W - MARGIN, y, { align: 'right' });
  doc.setFont('helvetica', 'normal');

  // Notes under the total (e.g. "prices excl. VAT", validity sentence).
  if (Array.isArray(notes) && notes.length) {
    y += 8;
    doc.setFontSize(8.5);
    doc.setTextColor(120);
    for (const note of notes.filter(Boolean)) {
      doc.text(String(note), MARGIN, y);
      y += 4.5;
    }
    doc.setTextColor(20);
  }

  // Letter-style sign-off ("Freundliche Grüsse" + company name).
  if (closing) {
    y += 12;
    doc.setFontSize(10);
    doc.text(String(closing), MARGIN, y);
    if (settings?.companyName) {
      doc.setFont('helvetica', 'bold');
      doc.text(String(settings.companyName), MARGIN, y + 6);
      doc.setFont('helvetica', 'normal');
    }
  }

  seitenzahlen(doc, t);
  return { doc, filename };
}

function buildOrderPdf({ order, customer, settings, t }) {
  return buildSalesDocPdf({
    settings, t,
    title: t('order.pdf.heading') || 'Order confirmation',
    ref: order.orderNumber,
    party: customer,
    partyLabel: t('order.pdf.to') || 'Customer',
    meta: [
      [t('col.date') || 'Date', formatDate(order.orderDate)],
      ...(order.requiredDate ? [[t('detail.metric.expected') || 'Expected', formatDate(order.requiredDate)]] : []),
    ],
    lines: order.lines,
    currency: order.currency,
    total: order.totalAmount,
    closing: t('quote.pdf.closing') || 'Kind regards',
    draftMark: order.status === 'DRAFT' ? (t('pdf.draftMark') || 'DRAFT') : null,
    filename: `Auftragsbestätigung_${order.orderNumber}.pdf`,
  });
}

function buildQuotePdf({ quote, customer, settings, t }) {
  return buildSalesDocPdf({
    settings, t,
    title: t('quote.pdf.heading') || 'Quote',
    ref: quote.quoteNumber,
    party: customer,
    partyLabel: t('quote.pdf.to') || 'Quote to',
    meta: [
      [t('col.date') || 'Date', formatDate(quote.quoteDate)],
      ...(quote.validUntil ? [[t('detail.metric.validUntil') || 'Valid until', formatDate(quote.validUntil)]] : []),
    ],
    intro: t('quote.pdf.intro'),
    lines: quote.lines,
    currency: quote.currency,
    total: quote.totalAmount,
    totals: Number(quote.taxAmount) > 0 ? { net: quote.netAmount, tax: quote.taxAmount } : null,
    notes: [
      // With VAT broken out above, the "excl. VAT" note would contradict the total.
      Number(quote.taxAmount) > 0 ? null : t('quote.pdf.vatNote', { currency: quote.currency || 'CHF' }),
      quote.validUntil ? t('quote.pdf.validity', { date: formatDate(quote.validUntil) }) : null,
    ],
    closing: t('quote.pdf.closing') || 'Kind regards',
    draftMark: quote.status === 'DRAFT' ? (t('pdf.draftMark') || 'DRAFT') : null,
    filename: `Offerte_${quote.quoteNumber}.pdf`,
  });
}

// Purchase order to a supplier - same Swiss letter layout as quote/order.
// PO lines carry sku/productName (no description column) and unitCost, so
// they are adapted to the shared table shape here; suppliers store a plain
// `address`, mapped onto the partyBlock's billingAddress slot.
function buildPurchaseOrderPdf({ po, supplier, settings, t }) {
  const party = supplier
    ? { ...supplier, billingAddress: supplier.billingAddress || supplier.address }
    : null;
  const lines = (po.lines || []).map((l) => ({
    description: l.sku ? `${l.sku} - ${l.productName}` : (l.productName || String(l.productId || '').slice(0, 8)),
    quantity: l.quantity,
    unitPrice: l.unitCost,
    lineAmount: l.lineAmount,
  }));
  return buildSalesDocPdf({
    settings, t,
    title: t('po.pdf.heading') || 'Purchase order',
    ref: po.poNumber,
    party,
    partyLabel: t('po.pdf.to') || 'Supplier',
    meta: [
      [t('col.date') || 'Date', formatDate(po.orderDate)],
      ...(po.expectedDate ? [[t('detail.metric.expected') || 'Expected', formatDate(po.expectedDate)]] : []),
    ],
    intro: t('po.pdf.intro'),
    lines,
    currency: po.currency,
    total: po.totalAmount,
    closing: t('quote.pdf.closing') || 'Kind regards',
    draftMark: po.status === 'DRAFT' || po.status === 'PENDING_APPROVAL' ? (t('pdf.draftMark') || 'DRAFT') : null,
    filename: `Bestellung_${po.poNumber}.pdf`,
  });
}

// Swiss payslip (Lohnabrechnung): earnings block → gross, deductions block →
// bold net. One page per employee, confidential footer. `run` supplies the
// period, `payslip` carries the employee snapshot (name, number, amounts).
async function buildPayslipPdf({ payslip, run, settings, t }) {
  const jsPDF = await loadJsPdf();
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const period = `${String(run.periodMonth).padStart(2, '0')}/${run.periodYear}`;
  let y = header(doc, settings, t('payslip.pdf.heading') || 'Payslip', period);
  const name = [payslip.firstName, payslip.lastName].filter(Boolean).join(' ');
  y = partyBlock(doc, y, t('payslip.pdf.employee') || 'Employee', { name }, [
    [t('payslip.pdf.employeeNumber') || 'Employee no.', payslip.employeeNumber || '-'],
    [t('payslip.pdf.period') || 'Period', period],
  ]);
  y = hr(doc, y);

  const cur = payslip.currency;
  const row = (label, amount, { bold = false, sign = '' } = {}) => {
    y = raum(doc, y, 6);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 10.5 : 9.5);
    doc.text(label, MARGIN, y);
    doc.text(`${sign}${formatCurrency(amount, cur)}`, PAGE_W - MARGIN, y, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    y += 6;
  };

  const components = payslip.components || [];
  const allowanceLines = components.filter((c) => c.kind === 'ALLOWANCE');
  const deductionLines = components.filter((c) => c.kind === 'DEDUCTION');

  row(t('payslip.pdf.baseSalary') || 'Base salary', payslip.baseSalary);
  if (allowanceLines.length > 0) {
    for (const line of allowanceLines) {
      row(line.label, line.amount, { sign: '+ ' });
    }
  } else if (Number(payslip.allowancesTotal) > 0) {
    row(t('payslip.pdf.allowances') || 'Allowances', payslip.allowancesTotal, { sign: '+ ' });
  }
  row(t('payslip.pdf.gross') || 'Gross', payslip.grossAmount, { bold: true });
  y += 2;
  // Swiss payroll: social insurance lines (AHV/IV/EO, ALV, NBU, BVG) are the
  // itemised deductions; a tax line only appears on legacy payslips (> 0).
  if (Number(payslip.taxAmount) > 0) {
    row(t('payslip.pdf.tax') || 'Withholding tax', payslip.taxAmount, { sign: '− ' });
  }
  if (deductionLines.length > 0) {
    for (const line of deductionLines) {
      row(line.label, line.amount, { sign: '− ' });
    }
  } else if (Number(payslip.deductionsTotal) > 0) {
    row(t('payslip.pdf.deductions') || 'Deductions', payslip.deductionsTotal, { sign: '− ' });
  }
  /* Der Nettobetrag ist die einzige Zahl, wegen der jemand diesen Zettel
     ueberhaupt anschaut. Er wird nie vom Blattrand abgeschnitten. */
  y = raum(doc, y, 5 + 12);
  y = hr(doc, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(t('payslip.pdf.net') || 'Net pay', MARGIN, y);
  doc.text(formatCurrency(payslip.netAmount, cur), PAGE_W - MARGIN, y, { align: 'right' });
  doc.setFont('helvetica', 'normal');

  y += 12;
  doc.setFontSize(8.5);
  doc.setTextColor(120);
  doc.text(t('payslip.pdf.confidential') || 'Confidential - for the recipient only.', MARGIN, y);
  doc.setTextColor(20);

  seitenzahlen(doc, t);
  const safeName = (payslip.employeeNumber || name || 'MA').replace(/[^\w-]+/g, '_');
  return { doc, filename: `Lohnabrechnung_${safeName}_${run.periodYear}-${String(run.periodMonth).padStart(2, '0')}.pdf` };
}

function attachmentFrom({ doc, filename }) {
  // jsPDF datauristring: "data:application/pdf;base64,XXXX" → take the base64 part.
  const dataUri = doc.output('datauristring');
  return { base64: dataUri.slice(dataUri.indexOf(',') + 1), name: filename };
}

export function downloadInvoicePdf(args) {
  return downloadPdf(buildInvoicePdf, args);
}
export function downloadCreditNotePdf(args) {
  return downloadPdf(buildCreditNotePdf, args);
}
export function downloadDeliveryPdf(args) {
  return downloadPdf(buildDeliveryPdf, args);
}
export function downloadOrderPdf(args) {
  return downloadPdf(buildOrderPdf, args);
}
export function downloadQuotePdf(args) {
  return downloadPdf(buildQuotePdf, args);
}
export function invoicePdfAttachment(args) {
  return buildInvoicePdf(args).then(attachmentFrom);
}
export function quotePdfAttachment(args) {
  return buildQuotePdf(args).then(attachmentFrom);
}
export function orderPdfAttachment(args) {
  return buildOrderPdf(args).then(attachmentFrom);
}
export function deliveryPdfAttachment(args) {
  return buildDeliveryPdf(args).then(attachmentFrom);
}
export function downloadPurchaseOrderPdf(args) {
  return downloadPdf(buildPurchaseOrderPdf, args);
}
export function downloadPayslipPdf(args) {
  return downloadPdf(buildPayslipPdf, args);
}
export function previewQuotePdf(args) {
  return previewPdf(buildQuotePdf, args);
}
export function previewOrderPdf(args) {
  return previewPdf(buildOrderPdf, args);
}
export function previewPurchaseOrderPdf(args) {
  return previewPdf(buildPurchaseOrderPdf, args);
}
export function previewInvoicePdf(args) {
  return previewPdf(buildInvoicePdf, args);
}
export function previewDeliveryPdf(args) {
  return previewPdf(buildDeliveryPdf, args);
}
export function previewCreditNotePdf(args) {
  return previewPdf(buildCreditNotePdf, args);
}
export function previewDunningPdf(args) {
  return previewPdf(buildDunningPdf, args);
}
export function previewPayslipPdf(args) {
  return previewPdf(buildPayslipPdf, args);
}
export function purchaseOrderPdfAttachment(args) {
  return buildPurchaseOrderPdf(args).then(attachmentFrom);
}
export function downloadDunningPdf(args) {
  return downloadPdf(buildDunningPdf, args);
}
export function dunningPdfAttachment(args) {
  return buildDunningPdf(args).then(attachmentFrom);
}

/**
 * Die Bauer selbst - fuer Pruefläufe.
 *
 * Ohne diesen Ausgang liesse sich der Seitenumbruch nur ueber die fertige
 * Datei pruefen, und in diesem Projekt haben Attrappen an genau solchen
 * Stellen schon dreimal gelogen. Hier kommt das ECHTE Dokument heraus, mit
 * echter Seitenzahl und echtem Inhalt.
 */
export const pdfBauer = {
  invoice: buildInvoicePdf,
  creditNote: buildCreditNotePdf,
  delivery: buildDeliveryPdf,
  order: buildOrderPdf,
  quote: buildQuotePdf,
  purchaseOrder: buildPurchaseOrderPdf,
  payslip: buildPayslipPdf,
};
